import React, { useEffect, useState } from 'react';
import Modal from 'components/molecules/modal';
import { EssentialInfo, AdditionalInfo } from './components';
import { Loading, Button } from 'components/atoms';
import { ThirdPartyDataConfigDictionary } from 'services/getThirdPartyConfiguration';
import { useForm, FormProvider } from 'react-hook-form';
import { FixedWidthButton, ButtonWrapper, FormSubheading } from 'form/form.styles';
import CollapsibleSection from 'components/molecules/collapsible-section';
import { useDebounce, useFetch } from 'hooks';
import { useAuth } from 'auth';
import { useConfig } from 'config';
import {
  individualAdditionalFields,
  companyAdditionalFields,
} from 'auth/constants/thirdPartyAdditionalFields';
import { initialState, validationSchema } from './validations';
import { ThirdPartyFormValues } from './types';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormBody } from './ThirdPartyFormModal.styles';
import { ThirdPartyCreateModelResponse, ThirdPartyType } from 'types';
import { useTerminologyConfig, useUserSettings } from 'context/DataContext';
import dayjs from 'dayjs';

type ThirdPartyFormModalProps = {
  isOpen?: boolean;
  thirdParty?: Record<string, any> | undefined;
  onClose?: () => void;
  onSuccess?: (thirdParty: ThirdPartyCreateModelResponse) => void;
  isChild: boolean;
};

const ThirdPartyFormModal: React.FC<ThirdPartyFormModalProps> = ({
  isOpen,
  thirdParty,
  onClose,
  onSuccess,
  isChild = false,
}) => {
  const terminologyConfig = useTerminologyConfig();
  const { tokenInfo } = useAuth();
  const { vantageWebApi } = useConfig();
  const { gatewayUserId } = useUserSettings();
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(true);
  const [selectedConfiguration, setSelectedConfiguration] =
    useState<ThirdPartyDataConfigDictionary | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const { data: thirdPartyConfigurations, loading: loadingThirdPartyConfigurations } = useFetch<
    Record<string, ThirdPartyDataConfigDictionary>
  >(`${vantageWebApi}/client/thirdparty-fields-configurations`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
    lazy: false,
  });

  const { trigger: internalIdValidateFetchLazy, loading: internalIdValidateLoading } = useFetch<
    boolean,
    unknown,
    'value'
  >(`${vantageWebApi}/thirdparty/internalId/validate`, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    },
    lazy: true,
    method: 'GET',
  });

  const { trigger: triggerThirdPartyRequest, loading: loadingRequest } = useFetch(
    `${vantageWebApi}/thirdParty/${!!thirdParty ? `${thirdParty.id}/edit` : 'create'}`,
    {
      method: 'POST',
      lazy: true,
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${tokenInfo.accessToken}`,
      },
    }
  );

  const methods = useForm({
    mode: 'all',
    resolver: yupResolver(
      validationSchema({
        thirdPartyDataConfig: selectedConfiguration,
        terminologyConfig,
      })
    ),
    defaultValues: { ...initialState },
  });

  const internalIdWatch = methods.watch('internalId');
  const debounceInternalId = useDebounce(internalIdWatch, 500);

  useEffect(() => {
    if (!debounceInternalId?.trim()) {
      methods.clearErrors('internalId');
      return;
    }
    internalIdValidateFetchLazy({
      queryParams: { value: debounceInternalId.trim() },
    }).then((res) => {
      if (res) {
        methods.clearErrors('internalId');
      } else {
        methods.setError('internalId', {
          type: 'manual',
          message: `${terminologyConfig?.internalIdSingularNaming} already in use for another ${terminologyConfig?.thirdPartySingularNaming}.`,
        });
      }
    });
  }, [debounceInternalId, internalIdValidateFetchLazy, methods, terminologyConfig]);

  const thirdPartyType = methods.watch('type');
  const isBusiness = thirdPartyType === ThirdPartyType.Business;

  const checkAdditionalMandatoryFields = (
    fields: string[],
    currentConfig: ThirdPartyDataConfigDictionary
  ) => {
    return fields.some((fieldName) => currentConfig[fieldName]?.isMandatory === true);
  };

  const defaultOwnerId = selectedConfiguration?.autoAssignDefaultInternalOwner?.configurationValue;

  useEffect(() => {
    if (defaultOwnerId && gatewayUserId != defaultOwnerId) {
      methods.resetField('ownerIds', {
        defaultValue: [String(defaultOwnerId), String(gatewayUserId)],
      });
    } else {
      methods.resetField('ownerIds', {
        defaultValue: [String(gatewayUserId)],
      });
    }
  }, [gatewayUserId, methods, defaultOwnerId]);

  useEffect(() => {
    if (thirdPartyConfigurations) {
      const currentConfig = thirdPartyConfigurations[thirdPartyType.toLocaleLowerCase()];
      setSelectedConfiguration(currentConfig);
      setShowAdditionalInfo(
        !checkAdditionalMandatoryFields(
          isBusiness ? companyAdditionalFields : individualAdditionalFields,
          currentConfig
        )
      );
    }
  }, [thirdPartyType, thirdPartyConfigurations, isBusiness]);

  const onSubmit = async (formData: ThirdPartyFormValues) => {
    setErrorMessage(null);
    try {
      const result = await triggerThirdPartyRequest({
        body: JSON.stringify({
          type: formData.type,
          name: formData?.name?.trim(),
          alternativeName: formData?.alternativeName?.trim(),
          internalId: formData?.internalId?.trim(),
          duns: formData?.duns || null,
          primaryCountry: formData?.primaryCountry === 'DEFAULT' ? null : formData?.primaryCountry,
          countryTwo: formData?.countryTwo === 'DEFAULT' ? null : formData?.countryTwo,
          countryThree: formData?.countryThree === 'DEFAULT' ? null : formData?.countryThree,
          companyUrl: formData?.companyUrl,
          addressLineOne: formData.addressLineOne,
          city: formData.city,
          county: formData.county,
          zipCode: formData.zipCode,
          ownerIds: formData.ownerIds,
          ...(isBusiness
            ? {
                registrationNumber: formData.registrationNumber,
                swift: formData.swift,
                companyUrl: formData.companyUrl,
              }
            : {
                gender: formData.gender,
                dateOfBirth: formData?.dateOfBirth
                  ? dayjs(formData?.dateOfBirth, 'DD/MM/YYYY').format('YYYY-MM-DD[T]00:00:00[Z]')
                  : null,
                nationality: formData.nationality === 'DEFAULT' ? null : formData?.nationality,
                passportNumber: formData.passportNumber,
                nationalIDNumber: formData.nationalIDNumber,
                otherIDNumber: formData.otherIDNumber,
              }),
          isChild: isChild,
        }),
      });

      if (result) {
        if (onSuccess) {
          onSuccess(result);
        }
        handleOnCloseCreateThirdParty();
      } else {
        setErrorMessage('Failed to create third party. Please try again.');
      }
    } catch (error: any) {
      setErrorMessage(
        error?.message || 'An unexpected error occurred while creating the third party.'
      );
    }
  };

  const additionalInfoLabelValue = isBusiness
    ? 'Add additional company details (Address, Registration number, etc.)'
    : 'Add additional individual details (Address, DOB, etc.)';

  const handleOnCloseCreateThirdParty = () => {
    if (onClose) onClose();
    methods.reset({ ...initialState });
  };

  return (
    <Modal
      isOpen={isOpen}
      customOverlayWidth="800px"
      onClose={handleOnCloseCreateThirdParty}
      removePadding
      title={`${!!thirdParty ? 'Edit' : 'Create'} a ${
        !!thirdParty ? '' : 'new'
      } ${terminologyConfig?.thirdPartySingularNaming.toLowerCase()}`}
    >
      <FormBody>
        {loadingThirdPartyConfigurations || !selectedConfiguration ? (
          <Loading />
        ) : (
          <FormProvider {...methods}>
            <FormSubheading>{terminologyConfig?.thirdPartySingularNaming} Type</FormSubheading>
            <form onSubmit={methods.handleSubmit(onSubmit)}>
              <EssentialInfo
                isEdit={!thirdParty}
                thirdPartyDataConfig={selectedConfiguration}
                setShowAdditionalInfo={setShowAdditionalInfo}
              />
              <CollapsibleSection
                label={additionalInfoLabelValue}
                collapsed={showAdditionalInfo}
                setCollapsed={setShowAdditionalInfo}
              >
                <AdditionalInfo thirdPartyDataConfig={selectedConfiguration} />
              </CollapsibleSection>
              {errorMessage && <div style={{ color: 'red', marginBottom: 8 }}>{errorMessage}</div>}
              <ButtonWrapper justifyContent="end" sticky>
                <Button
                  disabled={loadingRequest}
                  text
                  type="button"
                  onClick={handleOnCloseCreateThirdParty}
                >
                  Cancel
                </Button>
                <FixedWidthButton
                  disabled={
                    loadingRequest ||
                    internalIdValidateLoading ||
                    (internalIdWatch && internalIdWatch !== debounceInternalId)
                  }
                  secondary
                  type="submit"
                >
                  {loadingRequest ? <Loading /> : <>Next Step</>}
                </FixedWidthButton>
              </ButtonWrapper>
            </form>
          </FormProvider>
        )}
      </FormBody>
    </Modal>
  );
};

export default ThirdPartyFormModal;
